'use client';

import PlusIcon from '@/components/icons/PlusIcon';
import { useProductList } from '@/components/product-list-context';
import useNotifyMe from '@/hooks/useNotifyMe';
import { addItemToCart } from '@/services/cart';
import useCart from '@/store/cart.store';
import useCheckout from '@/store/checkout.store';
import useGlobalStore from '@/store/global.store';
import {
  //  checkStockAvailability,
  getNeededProductData,
} from '@/utils/global';
import { event_add_to_cart } from '@/utils/gtm';
import { getCookies, setCookie } from 'cookies-next';
import { useSession } from 'next-auth/react';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { toast } from '@/utils/toast';
import CartIcon from '../icons/CartIcon';
import NotifyMeIcon from '../icons/notify-me-icon';

const AddToCart = ({
  fromPdp,
  product,
  fromCartPopUp,
  setAdded = false,
  pdpRef,
  fromNewPdp,
  isFromTopSelling = false,
  qty = 1,
}) => {
  const {
    // defaultSourceIds,
    notifyProducts,
  } = useGlobalStore();
  const notify = useNotifyMe();
  const [loading, setLoading] = useState(false);
  const t = useTranslations('product');
  const locale = useLocale();
  // Null on the PDP, where the button belongs to no list.
  const productList = useProductList();
  const cart = useCart((store) => store);
  const clearCheckout = useCheckout((state) => state.clearCheckout);
  const { data: session } = useSession();

  // ✅ check if product already in cart
  // const singleProduct = cart.cartItems.find(
  //   (item) => item.innova_url === product?.innova_url
  // );
  // if (singleProduct) {
  //   return null; // hide button if item exists
  // }

  const isNotifiedBefore =
    notifyProducts?.findIndex(
      (info) =>
        info?.entity_id === product?.entity_id &&
        session?.user?.id === info?.userId
    ) !== -1;

  // const is_stock = checkStockAvailability(
  //   product?.stock_innova || {},
  //   defaultSourceIds
  // );

  const is_stock = product?.stock?.is_in_stock && product?.stock?.qty > 0;

  const handleAddToCart = async () => {
    setLoading(true);
    const { quote, mage } = getCookies();
    const bundleProducts =
      product?.bundle_product_options
        ?.map((b) => ({
          option_id: b?.option_id,
          option_qty: Number(
            b.product_links?.at(0)?.bundle_child_default_stock
          ),
          option_selections: [b.product_links?.at(0)?.id],
        }))
        ?.flat() || [];
    try {
      const res = await addItemToCart({
        sku: Array.isArray(product?.sku) ? product?.sku[0] : product?.sku,
        qty,
        quote,
        mage,
        bundleItems: bundleProducts,
      });

      if (res?.status === 200) {
        // Handle new quote
        if (quote !== res?.data?.quoteId) {
          cart.resetCart();
          clearCheckout();
          setCookie('quote', res?.data?.quoteId);
        }

        const productData = getNeededProductData(product);
        cart?.addItem({
          ...productData,
          ...res?.data?.data,
          qty: res?.data?.data?.qty,
          productFeesFromCart: [],
        });

        // `qty` is the quantity this click adds, which is the delta the spec
        // asks for — the PDP stepper only moved local state until now.
        event_add_to_cart({
          product,
          quantity: qty,
          locale,
          list: productList?.list,
        });

        setLoading(false);

        if (!fromCartPopUp) cart?.openCartPopup();
        if (setAdded) setAdded(true);
      } else {
        toast.error(
          t(res?.error?.error?.message?.replaceAll('.', '') || 'ErrorAddToCart')
        );
      }
    } catch {}

    setLoading(false);
  };

  const handleNotifyMe = () => {
    if (session?.user) {
      notify?.handleNotifyMe(session, product?.entity_id, false);
    } else {
      notify?.push(`/login?entity_id=${product?.entity_id}`);
    }
  };

  // === Variants ===
  if (isFromTopSelling) {
    return (
      <button
        type='button'
        disabled={loading}
        onClick={is_stock ? handleAddToCart : handleNotifyMe}
        className={`${!is_stock ? 'bg-gray-400 hover:bg-gray-400/80 disabled:bg-gray-400/50' : 'bg-primary hover:bg-primary-700 disabled:bg-primary/50'} hover:opacity-90' grid h-8 w-8 place-content-center rounded-full text-lg text-white`}
        aria-label={t('Add to cart')}
      >
        {loading ? (
          <div className='size-4 animate-spin rounded-full border-t border-r border-[#aaa] py-1' />
        ) : is_stock ? (
          <PlusIcon className='fill-white' />
        ) : (
          <NotifyMeIcon className='fill-white' />
        )}
      </button>
    );
  }

  if (fromPdp) {
    return (
      <button
        ref={pdpRef}
        onClick={is_stock ? handleAddToCart : handleNotifyMe}
        disabled={loading}
        className={
          fromNewPdp
            ? 'flex w-full items-center justify-center gap-1.5 rounded-md bg-[#1a2650] py-2.5 text-sm font-medium text-white'
            : 'border-primary max-md:bg-primary text-primary flex w-full items-center justify-center gap-2 rounded-xl border-2 bg-white px-3 py-3.5 font-medium duration-300 hover:scale-102 max-md:hidden max-md:text-white max-sm:py-2.5 max-sm:text-sm'
        }
      >
        {loading ? (
          <span
            className={`${fromNewPdp ? 'border-white' : 'border-primary'} size-4 animate-spin rounded-full border-t border-r py-1`}
          />
        ) : null}
        {is_stock ? (
          <>
            <CartIcon fromPDP />
            {t('Add to cart')}
          </>
        ) : !isNotifiedBefore ? (
          <>
            <NotifyMeIcon className='size-5 max-md:size-4.25 max-sm:size-3.5' />
            {t('Notify Me')}
          </>
        ) : (
          <>
            <NotifyMeIcon className='size-5 text-white max-md:size-4.25 max-sm:size-3.5' />
            {t('Notified Before')}
          </>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={is_stock ? handleAddToCart : handleNotifyMe}
      disabled={loading}
      className={`${is_stock ? 'bg-primary hover:bg-primary-700 disabled:bg-primary/50' : 'bg-gray-400 hover:bg-gray-400/80 disabled:bg-gray-400/50'} flex w-full items-center justify-center gap-1 rounded-xl px-3 py-3.5 font-bold text-white max-sm:py-2.5 max-sm:text-sm`}
    >
      {loading ? (
        <span className='size-4 animate-spin rounded-full border-t border-r border-white py-1' />
      ) : null}
      {is_stock ? (
        <>{t('Add to cart')}</>
      ) : !isNotifiedBefore ? (
        <>
          <NotifyMeIcon className='size-5 max-md:size-4.25 max-sm:size-3.5' />
          {t('Notify Me')}
        </>
      ) : (
        <>
          <NotifyMeIcon className='size-5 text-white max-md:size-4.25 max-sm:size-3.5' />
          {t('Notified Before')}
        </>
      )}
    </button>
  );
};

export default AddToCart;
